import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    CircularProgress,
    Alert,
    Stack
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { QuizService } from '../../services/modules/quiz';

interface Question {
    question_id: number;
    question_text: string;
    options: string[];
}

interface QuizAttemptResponse {
    questions: Question[];
    time_per_question: number;
}

const Quiz: React.FC = () => {
    const { t } = useTranslation();
    const { attemptId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState<{ [key: number]: number }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [startTime, setStartTime] = useState<Date | null>(null);
    const [timePerQuestion, setTimePerQuestion] = useState<number>(0);

    const handleSubmit = useCallback(async () => {
        try {
            if (!user?.token || !attemptId) return;
            setSubmitting(true);
            
            // Convert answers object to array format
            const answersArray = questions.map((_, index) => {
                const answer = answers[index];
                return answer !== undefined ? answer : -1;
            });
            
            await QuizService.submitQuizAttempt(parseInt(attemptId), answersArray, user.token);
            navigate('/student/attempts');
        } catch (err) {
            setError(t('quiz.submitError'));
            setSubmitting(false);
        }
    }, [user?.token, attemptId, questions, answers, navigate]);

    useEffect(() => {
        const loadQuiz = async () => {
            try {
                if (!user?.token || !attemptId) return;
                const response: QuizAttemptResponse = await QuizService.getQuizAttempt(parseInt(attemptId), user.token);
                if (response.questions) {
                    setQuestions(response.questions);
                    setStartTime(new Date());
                    setTimePerQuestion(response.time_per_question);
                    // Set initial time
                    setTimeLeft(response.questions.length * response.time_per_question);
                }
            } catch (err) {
                setError(t('quiz.loadError'));
            } finally {
                setLoading(false);
            }
        };

        loadQuiz();
    }, [user?.token, attemptId]);

    useEffect(() => {
        if (!startTime) return;

        const timer = setInterval(() => {
            const now = new Date();
            const elapsedSeconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
            const newTimeLeft = (questions.length * timePerQuestion) - elapsedSeconds;
            
            if (newTimeLeft <= 0) {
                clearInterval(timer);
                handleSubmit();
            } else {
                setTimeLeft(newTimeLeft);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [startTime, questions.length, handleSubmit, timePerQuestion]);

    const formatTime = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes} ${minutes === 1 ? t('quiz.minute') : t('quiz.minutes')} ${remainingSeconds} ${remainingSeconds === 1 ? t('quiz.second') : t('quiz.seconds')}`;
    };

    const handleAnswerChange = (value: string) => {
        const newAnswers = { ...answers };
        newAnswers[currentQuestion] = parseInt(value);
        setAnswers(newAnswers);
    };

    const handleNext = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>{t('quiz.loading')}</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!questions.length) {
        return (
            <Box p={3}>
                <Alert severity="warning">{t('quiz.noQuestions')}</Alert>
            </Box>
        );
    }

    const question = questions[currentQuestion];

    return (
        <Box p={3}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {t('quiz.questionProgress', { current: currentQuestion + 1, total: questions.length })}
                        </Typography>
                        <Typography 
                            variant="h6" 
                            color={timeLeft <= 60 ? "error" : "primary"}
                            sx={{ 
                                bgcolor: timeLeft <= 60 ? '#ffebee' : '#e3f2fd',
                                px: 2,
                                py: 1,
                                borderRadius: 1,
                                fontWeight: 'bold'
                            }}
                        >
                            {t('quiz.timeLeft', { time: formatTime(timeLeft) })}
                        </Typography>
                    </Box>

                    <FormControl component="fieldset">
                        <FormLabel component="legend">{question.question_text}</FormLabel>
                        <RadioGroup
                            value={answers[currentQuestion]?.toString() || ''}
                            onChange={(e) => handleAnswerChange(e.target.value)}
                        >
                            {question.options.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={index.toString()}
                                    control={<Radio />}
                                    label={option}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePrevious}
                            disabled={currentQuestion === 0}
                            sx={{ mt: 2 }}
                        >
                            {t('quiz.previous')}
                        </Button>
                        
                        {currentQuestion === questions.length - 1 ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={submitting || Object.keys(answers).length < questions.length}
                                sx={{ mt: 2 }}
                            >
                                {submitting ? t('quiz.submitting') : t('quiz.submit')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                disabled={answers[currentQuestion] === undefined}
                                sx={{ mt: 2, ml: 2 }}
                            >
                                {t('quiz.next')}
                            </Button>
                        )}
                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
};

export default Quiz;
